import React, { useState, useEffect } from 'react';
import { api } from '../../api/client';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import AdminLoading from './AdminLoading';
import AdminEmptyState from './AdminEmptyState';
import { IoPeopleOutline } from 'react-icons/io5';

export default function AdminStaff() {
  const [list, setList] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    email: '',
    password: '',
    name: '',
    role_id: '',
    is_active: true,
  });

  const load = () => {
    setLoading(true);
    Promise.all([api('/api/admin/staff'), api('/api/admin/roles')])
      .then(([staffData, rolesData]) => {
        setList(staffData);
        setRoles(rolesData || []);
        setError('');
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => load(), []);

  const handleOpenAdd = () => {
    setEditing(null);
    setForm({
      email: '',
      password: '',
      name: '',
      role_id: roles[0]?.id || '',
      is_active: true,
    });
    setOpen(true);
  };

  const handleOpenEdit = (staff) => {
    setEditing(staff);
    setForm({
      email: staff.email,
      password: '',
      name: staff.name || '',
      role_id: staff.role_id || '',
      is_active: staff.is_active !== 0,
    });
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const payload = {
        name: form.name.trim() || null,
        role_id: Number(form.role_id) || roles[0]?.id,
        is_active: form.is_active,
      };
      if (editing) {
        if (form.password && form.password.trim()) payload.password = form.password.trim();
        await api(`/api/admin/staff/${editing.id}`, { method: 'PUT', body: JSON.stringify(payload) });
      } else {
        if (!form.email?.trim() || !form.password?.trim()) {
          setError('Email and password required');
          return;
        }
        payload.email = form.email.trim();
        payload.password = form.password;
        await api('/api/admin/staff', { method: 'POST', body: JSON.stringify(payload) });
      }
      handleClose();
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this staff member?')) return;
    try {
      await api(`/api/admin/staff/${id}`, { method: 'DELETE' });
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const formatDate = (d) => (d ? new Date(d).toLocaleString() : '–');

  if (loading) return <AdminLoading />;

  return (
    <>
      <Card sx={{ boxShadow: 2, borderRadius: 2 }}>
        <CardHeader
          title="Staff"
          action={
            <Button variant="contained" onClick={handleOpenAdd} sx={{ textTransform: 'none' }}>
              Add staff
            </Button>
          }
        />
        <CardContent>
          {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}
          {list.length === 0 ? (
            <AdminEmptyState message="No staff." icon={IoPeopleOutline} />
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Last login</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {list.map((s) => (
                    <TableRow key={s.id} hover>
                      <TableCell>{s.name || '–'}</TableCell>
                      <TableCell>{s.email}</TableCell>
                      <TableCell>{s.role_name || '–'}</TableCell>
                      <TableCell>{s.is_active ? 'Active' : 'Inactive'}</TableCell>
                      <TableCell>{formatDate(s.last_login_at)}</TableCell>
                      <TableCell align="right">
                        <Button size="small" onClick={() => handleOpenEdit(s)}>Edit</Button>
                        <Button size="small" color="error" onClick={() => handleDelete(s.id)}>Delete</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit staff' : 'Add staff'}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              required={!editing}
              disabled={!!editing}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label={editing ? 'New password (leave blank to keep)' : 'Password'}
              type="password"
              value={form.password}
              onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
              required={!editing}
              autoComplete={editing ? 'new-password' : 'off'}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Name"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              sx={{ mb: 2 }}
            />
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Role</InputLabel>
              <Select
                value={form.role_id}
                label="Role"
                onChange={(e) => setForm((f) => ({ ...f, role_id: e.target.value }))}
              >
                {roles.map((r) => (
                  <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {editing && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.is_active}
                    onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
                  />
                }
                label="Active"
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button type="submit" variant="contained">{editing ? 'Update' : 'Add'}</Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
}
